// @ts-strict-ignore
import { useContext, useMemo } from 'react';
import {
  Badge,
  Box,
  Tabs,
  Text,
} from '@usealloy/component-library';

import {
  AccountsOverview,
  DataSources,
  OtherData,
  OutcomeReasons,
  Tags,
} from '../cards';
import { Row, Col } from '../common';
import { useEventTypeEvaluationsContext } from '../context';
import { RecentTransactions, WatchlistMatches } from '../tabs';
import { useGetWatchlistMatches } from '../../watchlist-review/hooks/getWatchlistMatches';
import { UserContext } from '~/dashboard/context';
import { ReviewTabCount } from '../../watchlist-review/styles';
import PartialDetails from '../../PartialDetails/PartialDetails';
import { useRiskData } from '../../RiskRatingCard/useRiskData';
import { RiskRatingCard } from '../../RiskRatingCard/RiskRatingCard';
import { hasDataSourcesData, hasWatchlistServiceConfigured } from '../../utils/cardDataChecks';
import { shouldRenderCard } from '../../utils/shouldRenderCard';

export const TransactionTabs = () => {
  const { evaluation, isInvestigationPage } = useEventTypeEvaluationsContext();
  const {
    evaluation_token: evaluationToken,
    entity_token: entityToken,
  } = evaluation;

  const { typhonDomain, featureFlags, permissions } = useContext(UserContext);
  const hasReadApplicationVersionsPermission = Boolean(permissions?.['READ:ApplicationVersions:null']);
  const hideEmptyCardsEnabled = Boolean(featureFlags?.removeEmptyCards);

  const [{
    data: { matches = [] },
    isLoading: isLoadingWatchlistMatches,
  }] = useGetWatchlistMatches({
    evaluations: [{
      evaluationToken,
      entityToken,
    }],
    typhonDomain,
    showLatestDecisions: true,
  });

  /* ---------- CRA Logic ---------- */
  const {
    riskFactors,
    riskFormula,
    riskRangeValues,
    riskRatingColor,
    riskRatingLabel,
    showRiskTab: shouldRenderRiskTab,
    totalRisk,
  } = useRiskData({
    riskScoreData: evaluation.final_response.summary?.risk_score,
  });

  /* ---------- vvvvvvvvv ---------- */

  const overviewContent = (
    <Row>
      <Col>
        {evaluation.result === 'partial'
          && evaluation.final_response?.warnings
          && <PartialDetails evaluation={evaluation} />}
        <AccountsOverview />
      </Col>
      <Col>
        <OutcomeReasons />
        <Tags />
        {shouldRenderCard(hasDataSourcesData(evaluation), hideEmptyCardsEnabled)
          && <DataSources />}
        <OtherData />
      </Col>
    </Row>
  );

  const tabs = useMemo(() => {
    const reviewMatchesLength = matches
      ? matches
        .filter(({ status, is_suppressed }) => (status === 'Needs Review' && !is_suppressed))
        .length
      : 0;

    const allTabs: Array<{
      label: React.ReactNode;
      value: string;
      content: React.ReactNode;
    }> = [];

    if (!isInvestigationPage) {
      allTabs.push({
        label: 'Transactions',
        value: 'transactions',
        content: <RecentTransactions />,
      });
    }

    // Only hide Watchlist tab if feature flag is ON and no watchlist service is configured
    if (shouldRenderCard(hasWatchlistServiceConfigured(evaluation), hideEmptyCardsEnabled)) {
      allTabs.push({
        label: (
          <Box direction="row" gap={1}>
            Watchlist Matches
            {!isLoadingWatchlistMatches && reviewMatchesLength > 0 && (
              <ReviewTabCount status="danger">
                <Box justifyContent="center" alignItems="center">
                  {reviewMatchesLength}
                </Box>
              </ReviewTabCount>
            )}
          </Box>
        ),
        value: 'watchlist',
        content: <WatchlistMatches evaluation={evaluation} />,
      });
    }
    if (shouldRenderRiskTab) {
      allTabs.push({
        label: (
          <Box>
            <Text style={{ color: 'var(--cl-text-secondary)' }}>Risk Score</Text>
            {riskRatingColor && riskRatingLabel && (
              <Badge outline backgroundColor={riskRatingColor}>{riskRatingLabel}</Badge>
            )}
          </Box>
        ),
        value: 'risk-score',
        content: (
          <RiskRatingCard
            canViewRiskFormula={hasReadApplicationVersionsPermission}
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            evaluationType={evaluation.evaluation_type}
            riskFactors={riskFactors}
            riskOutputFormula={riskFormula}
            riskRanges={riskRangeValues}
            riskValue={totalRisk}
            riskValueColor={riskRatingColor}
            riskValueLabel={riskRatingLabel}
            variant="entityRisk"
            workflowToken={evaluation.application_token}
            workflowVersion={evaluation.public_version}
          />
        ),
      });
    }


    // Only show Overview if there are other tabs.
    if (allTabs.length > 0) {
      allTabs.unshift({
        label: 'Overview',
        value: 'overview',
        content: overviewContent,
      });
    }

    return allTabs;
  }, [
    evaluation,
    featureFlags,
    isInvestigationPage,
    isLoadingWatchlistMatches,
    matches,
    shouldRenderRiskTab,
  ]);

  const mainContent = tabs.length > 0
    ? (
      <Tabs
        style={{ maxWidth: '100%', minWidth: '100%' }}
        tabs={tabs}
      />
    )
    : (
      overviewContent
    );

  if (isInvestigationPage) {
    return (
      <Box style={{ maxWidth: '100%', minWidth: '100%' }} gap={3}>
        {mainContent}
      </Box>
    );
  }

  return mainContent;
};
